import Fuse from 'fuse.js';
import { VCPM } from '../models/project.model';

export interface EsxiServerFilterCriteria {
  vendor?: string;
  vendorId?: string;      // takes precedence over vendor when present - ids are the correct semantic key
  serverModel?: string;
  generation?: string;
  generationId?: string;  // takes precedence over generation when present
}

export interface EsxiServerGeneration {
  id: string;
  name: string;
}

export interface EsxiServerModel {
  name: string;
  generations: EsxiServerGeneration[];
}

// 3-level cascade tree (vendor -> model -> generation) - unlike ReadyNodeFilterResponse, there is no
// "profile"/tier level, since ESXi servers have no vSAN-style certification tiers.
export interface EsxiServerFilterResponse {
  name: string; // Vendor name from API
  id: string;   // Vendor ID from API
  models: EsxiServerModel[];
}

export interface EsxiServerCpu {
  cpuSeriesId: string;
  cpuModelSeries: string;
  minClockSpeed: number; // GHz
  maxClockSpeed: number; // GHz
  maxCores: number;
  minCores: number;
}

export interface EsxiServerDefaults {
  cpuSockets?: number;
  coresPerSocket?: number;
  cpuClockSpeed?: number;
  memory?: number;
}

// Item shape returned by POST /v1/servers/search and GET /v1/servers/{id}.
export interface EsxiServerApiResponse {
  id: string;
  vendor: string;
  vendorId: string;
  model: string;
  generation: string;
  generationId: string;
  cpuVendor: string;
  maxMemory: number; // GB
  supportedReleases: string[];
  maxCoresPerSocket: number;
  maxCpuSockets: number;
  vcgLink: string;
  cpu: EsxiServerCpu;
  defaults?: EsxiServerDefaults;
}

// Flat row shape returned by GET /v1/servers/summary - no nested `cpu` object, no vendorId/generationId.
export interface EsxiServerSummaryResponse {
  id: string;
  model: string;
  generation: string;
  cpuModelSeries: string;
  maxMemory: number;
  vendor: string;
  vcgLink: string;
  maxCoresPerSocket: number;
}

/**
 * Maps a POST /servers/search or GET /servers/{id} item to VCPM.EsxiServerSelection, flattening the
 * nested `cpu` object onto the row the same way ReadyNode's mapper flattens its primary cpus[0] entry.
 */
export function mapApiResponseToEsxiServer(apiData: EsxiServerApiResponse): VCPM.EsxiServerSelection {
  return {
    kind: 'esxi-server',
    ui_row_id: `esxi-${apiData.id}`,
    serverId: apiData.id?.toString() || '',
    vendor: apiData.vendor || '',
    vendorId: apiData.vendorId || '',
    serverModel: apiData.model || '',
    generation: apiData.generation || '',
    generationId: apiData.generationId || '',
    cpuVendor: apiData.cpuVendor || '',
    maxMemory: apiData.maxMemory || 0,
    maxCoresPerSocket: apiData.maxCoresPerSocket,
    maxCpuSockets: apiData.maxCpuSockets,
    supportedReleases: apiData.supportedReleases || [],
    detailsUrl: apiData.vcgLink || '',
    cpuSeriesId: apiData.cpu?.cpuSeriesId,
    cpuModelSeries: apiData.cpu?.cpuModelSeries,
    minClockSpeed: apiData.cpu?.minClockSpeed,
    maxClockSpeed: apiData.cpu?.maxClockSpeed,
    minCores: apiData.cpu?.minCores,
    maxCores: apiData.cpu?.maxCores,
    ...(apiData.defaults ? { defaults: { ...apiData.defaults } } : {}),
  };
}

/**
 * Maps a GET /servers/summary row to the same VCPM.EsxiServerSelection shape - only the fields the
 * flat summary carries are populated; vendorId/generationId/cpuVendor/supportedReleases/maxCpuSockets
 * and the detail-only CPU sub-fields are left undefined, same convention as mapSummaryResponseToReadyNode.
 */
export function mapSummaryResponseToEsxiServer(dto: EsxiServerSummaryResponse): VCPM.EsxiServerSelection {
  return {
    kind: 'esxi-server',
    ui_row_id: `esxi-${dto.id}`,
    serverId: dto.id?.toString() || '',
    vendor: dto.vendor || '',
    serverModel: dto.model || '',
    generation: dto.generation || '',
    maxMemory: dto.maxMemory || 0,
    maxCoresPerSocket: dto.maxCoresPerSocket,
    detailsUrl: dto.vcgLink || '',
    cpuModelSeries: dto.cpuModelSeries,
  };
}

/** Extracts unique vendors from the search-filters tree, deduping by id - the same vendor id has
 *  been observed appearing in more than one top-level entry in the real API response. */
export function getVendorsFromEsxiFilters(filters: EsxiServerFilterResponse[]): { id: string; name: string }[] {
  const vendorMap = new Map<string, string>();
  filters
    .filter((f) => f.name !== undefined && f.name !== null)
    .forEach((f) => {
      if (!vendorMap.has(f.id)) {
        vendorMap.set(f.id, f.name);
      }
    });
  return Array.from(vendorMap, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
}

/** Gets server model names for a specific vendor by ID, merging models across every top-level
 *  entry sharing that vendor id (see getVendorsFromEsxiFilters) and deduping model names. */
export function getModelsForVendor(filters: EsxiServerFilterResponse[], vendorId: string): string[] {
  const modelNames = new Set<string>();
  filters.filter((f) => f.id === vendorId).forEach((f) => f.models.forEach((m) => modelNames.add(m.name)));
  return Array.from(modelNames).sort();
}

/** Gets CPU generations for a specific vendor + server model, merging across every top-level
 *  vendor entry and every model entry sharing that vendor id / model name. */
export function getGenerationsForModel(
  filters: EsxiServerFilterResponse[],
  vendorId: string,
  serverModel: string,
): { id: string; name: string }[] {
  const generationMap = new Map<string, { id: string; name: string }>();
  filters
    .filter((f) => f.id === vendorId)
    .forEach((f) =>
      f.models
        .filter((m) => m.name === serverModel)
        .forEach((m) =>
          (m.generations || []).forEach((gen) => {
            if (!generationMap.has(gen.id)) {
              generationMap.set(gen.id, { id: gen.id, name: gen.name });
            }
          }),
        ),
    );

  return Array.from(generationMap.values()).sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Filters ESXi servers based on criteria (client-side filtering). Unlike ReadyNode's filterReadyNodes,
 * `serverModel` matches node.serverModel directly - the search-filters tree's models[].name and the
 * search/summary/detail responses' `model` field are the same flat identifier here, with no
 * vendor-prefix divergence to work around.
 */
export function filterEsxiServers(
  servers: VCPM.EsxiServerSelection[],
  criteria: EsxiServerFilterCriteria,
): VCPM.EsxiServerSelection[] {
  return servers.filter((node) => {
    if (criteria.vendorId ? node.vendorId !== criteria.vendorId : criteria.vendor && node.vendor !== criteria.vendor) {
      return false;
    }
    if (criteria.generationId
      ? node.generationId !== criteria.generationId
      : criteria.generation && node.generation !== criteria.generation) {
      return false;
    }
    if (criteria.serverModel && node.serverModel !== criteria.serverModel) {
      return false;
    }
    return true;
  });
}

// ---------------------------------------------------------------------------------------------------
// Fuzzy free-text search
// ---------------------------------------------------------------------------------------------------

const FREE_TEXT_SEARCH_KEYS = ['vendor', 'serverModel', 'cpuModelSeries', 'generation'];

// Memoized against the array IDENTITY it was built from - same 1-entry-cache rationale as
// searchReadyNodesFreeText (readynode.adapter.ts): rebuilding Fuse's index is O(catalog), too
// expensive to redo on every debounced keystroke, but the cached catalog array is stable for the
// lifetime of a context (EsxiServersDataService.getEsxiServerSummaries' shareReplay).
let cachedFuseSource: VCPM.EsxiServerSelection[] | null = null;
let cachedFuse: Fuse<VCPM.EsxiServerSelection> | null = null;

/**
 * Fuzzy free-text search over the catalog's identifying fields - vendor, device model, CPU model
 * series, and generation. Mirrors searchReadyNodesFreeText exactly: fuse.js does both matching and
 * ranking, `ignoreLocation: true` so a match late in a longer string isn't penalised.
 */
export function searchEsxiServersFreeText(servers: VCPM.EsxiServerSelection[], query: string): VCPM.EsxiServerSelection[] {
  const normalized = query?.trim();
  if (!normalized) {
    return servers; // no query - the caller's catalog order stands
  }
  if (cachedFuseSource !== servers || !cachedFuse) {
    cachedFuse = new Fuse(servers, {
      keys: FREE_TEXT_SEARCH_KEYS,
      threshold: 0.4,
      ignoreLocation: true,
    });
    cachedFuseSource = servers;
  }
  return cachedFuse.search(normalized).map((result) => result.item);
}
